import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, CreditCard } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import LeadBalanceCard from '@/components/LeadBalanceCard';
import { Listing } from '@/components/JobCard';
import { getLeadBalance, LeadBalance, EMPTY_BALANCE, formatPeriodEnd } from '@/lib/leadBalance';
import { money, tradeLabel, localizedSlug } from '@/data/constants';

/**
 * BUYER-SIDE TOOLING — shared by EVERY account that can purchase leads.
 *
 * `canBuyLeads()` in src/lib/roles.ts is true for BOTH subcontractors and
 * general contractors, the lead-purchase RLS policies allow both, and the
 * pricing page sells memberships to both. Previously these panels lived only
 * inside SubDashboard, so a GC could pay for a membership or a single lead and
 * then have nowhere in the app to see the balance, find the lead they bought or
 * check their plan. These are now ONE implementation imported by both
 * dashboards, so the buyer experience can never drift apart again.
 */

interface PurchaseRow {
  id: string;
  listing_id: string;
  amount: number;
  stage: string;
  created_at: string;
  purchase_method?: string | null;
}

const STAGES = ['purchased', 'contacted', 'estimate', 'won', 'lost'];

/** Membership row shape used by both sections below. */
interface SubRow {
  plan_code: string;
  amount: number;
  status: string;
}

const useMembership = () => {
  const { profile } = useAuth();
  const [balance, setBalance] = useState<LeadBalance>(EMPTY_BALANCE);
  const [sub, setSub] = useState<SubRow | null>(null);

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    getLeadBalance(profile.id)
      .then((b) => alive && setBalance(b))
      .catch(() => alive && setBalance(EMPTY_BALANCE));
    supabase
      .from('mkt_subscriptions')
      .select('*')
      .eq('user_id', profile.id)
      .in('status', ['active', 'past_due'])
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (alive) setSub(data && data[0] ? (data[0] as SubRow) : null);
      });
    return () => {
      alive = false;
    };
  }, [profile?.id]);

  return { balance, sub };
};

/** Remaining lead credits + renewal countdown. */
export const LeadBalanceSection: React.FC = () => {
  const { balance, sub } = useMembership();
  return <LeadBalanceCard balance={balance} amount={sub?.amount} />;
};

/**
 * "My purchased leads" pipeline + the membership summary.
 * Every lead the account has paid for, with the same stage tracking the
 * subcontractor dashboard has always had.
 */
export const MyLeadsSection: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const { balance, sub } = useMembership();
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [leadListings, setLeadListings] = useState<Record<string, Listing>>({});

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('mkt_lead_purchases')
        .select('*')
        .eq('buyer_id', profile.id)
        .order('created_at', { ascending: false });
      if (!alive) return;
      const rows = (data || []) as PurchaseRow[];
      setPurchases(rows);
      if (rows.length) {
        const { data: ls } = await supabase
          .from('mkt_listings')
          .select('*')
          .in('id', rows.map((r) => r.listing_id));
        if (!alive) return;
        const map: Record<string, Listing> = {};
        (ls || []).forEach((l: Listing) => (map[l.id] = l));
        setLeadListings(map);
      }
    })();
    return () => {
      alive = false;
    };
  }, [profile?.id]);

  const setStage = async (id: string, stage: string) => {
    await supabase.from('mkt_lead_purchases').update({ stage }).eq('id', id);
    setPurchases((p) => p.map((r) => (r.id === id ? { ...r, stage } : r)));
  };

  return (
    <>
      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Ticket className="h-5 w-5 text-orange-500" /> {t('dash.myLeads')}
        </h2>
        {purchases.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            {t('dash.noLeads')}
          </p>
        ) : (
          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {purchases.map((p) => {
              const l = leadListings[p.listing_id];
              return (
                <div
                  key={p.id}
                  className="flex flex-col gap-3 border-b border-slate-100 p-4 last:border-0 sm:flex-row sm:items-center"
                >
                  <div className="flex-1">
                    <Link
                      to={l ? path(`/jobs/${localizedSlug(l.slug, l.trade, lang)}`) : '#'}
                      className="font-bold text-slate-900 hover:text-orange-600"
                    >
                      {l?.title || t('common.loading')}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {l ? `${tradeLabel(l.trade, lang)} · ${l.city}, ${l.state}` : ''} ·{' '}
                      {/* Money only for a direct card purchase (the amount actually charged).
                          Credit unlocks show "1 credit" — their stored nominal amount is not a price. */}
                      {p.purchase_method === 'single_29' && p.amount > 0
                        ? money(p.amount)
                        : p.purchase_method === 'admin_grant' || p.amount === 0
                        ? t('pay.useIncluded')
                        : lang === 'es' ? '1 crédito' : '1 credit'}
                    </p>
                  </div>
                  <select
                    value={p.stage}
                    onChange={(e) => setStage(p.id, e.target.value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800"
                    aria-label={t('dash.myLeads')}
                  >
                    {STAGES.map((s) => (
                      <option key={s} value={s}>
                        {t(`dash.stage.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <CreditCard className="h-5 w-5 text-orange-500" /> {t('dash.mySubscription')}
        </h2>
        <div className="mt-5 flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center">
          <div>
            {sub ? (
              <>
                <p className="text-lg font-extrabold uppercase text-slate-900">{sub.plan_code}</p>
                <p className="text-sm text-slate-500">
                  {money(sub.amount)}
                  {t('pricing.perMonth')}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-600">{t('dash.noSubscription')}</p>
            )}
            <p className="mt-2 text-sm font-semibold text-emerald-700">
              {t('dash.includedLeads')}: {balance.remaining}
              {balance.included > 0 && <span className="text-slate-400"> / {balance.included}</span>}
            </p>
            {balance.periodEnd && (
              <p className="text-xs text-slate-400">
                {t('leads.renews')}: {formatPeriodEnd(balance.periodEnd, lang)}
              </p>
            )}
          </div>
          <Link
            to={path('/pricing')}
            className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
          >
            {t('dash.viewPlans')}
          </Link>
        </div>
      </section>
    </>
  );
};

export default MyLeadsSection;
